import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Session, SessionStatus } from '../types';
import { ApiError, sessionApi } from '../utils/api';

interface SessionContextType {
  session: Session | null;
  loading: boolean;
  error: string | null;
  refreshSession: () => Promise<void>;
  updateStatus: (status: SessionStatus) => Promise<void>;
  endSession: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);
  const isFetchingRef = useRef(false);

  const refreshSession = useCallback(async () => {
    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      return;
    }
    
    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);
      const data = await sessionApi.getCurrent();
      setSession(data);
      hasFetchedRef.current = true;
    } catch (err) {
      setSession(null);
      if (err instanceof ApiError && err.status === 401) {
        setError(null);
        hasFetchedRef.current = true;
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load session');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  const updateStatus = useCallback(async (status: SessionStatus) => {
    try {
      const data = await sessionApi.updateStatus(status);
      setSession(prev => prev ? { ...prev, ...data } : data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
      throw err;
    }
  }, []);

  const endSession = useCallback(async () => {
    try {
      await sessionApi.end();
      setSession(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to end session');
      throw err;
    }
  }, []);

  useEffect(() => {
    // Only fetch once on mount
    if (!hasFetchedRef.current && !isFetchingRef.current) {
      refreshSession();
    }
  }, [refreshSession]);

  return (
    <SessionContext.Provider
      value={{
        session,
        loading,
        error,
        refreshSession,
        updateStatus,
        endSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
